import type { Profile, SkillCategoryBlock } from "../../../../../client";
import { cn } from "../../../../../components/utils/cn";
import { faDisplay } from "@fortawesome/free-solid-svg-icons";
import { EditableSkillTag } from "./EditableSkillTag";

interface SkillsDisplayProps {
  skillCategoryBlocks: SkillCategoryBlock[];
  displayIcons?: boolean;
  pdfMode?: boolean;
  /** When provided, chips become inline-removable in the template editor. */
  onRemoveSkill?: (skillName: string) => void;
}

const CategoryBlock = ({
  category,
  displayIcons,
  pdfMode,
  onRemoveSkill,
}: {
  category: SkillCategoryBlock;
  displayIcons?: boolean;
  pdfMode?: boolean;
  onRemoveSkill?: (skillName: string) => void;
}) => {
  return (
    <div className="flex-1 min-w-0">
      <h3 className="text-md font-semibold mb-2">{category.title}</h3>
      <div className={cn("flex flex-wrap gap-2 rounded-lg flex-row")}>
        {category.skillTags
          ?.sort((a, b) => a.name.localeCompare(b.name))
          .map((skillTag) => (
            <div key={skillTag.name} className="inline-block">
              <EditableSkillTag
                skillTag={skillTag}
                hideIcon={!displayIcons}
                pdfMode={pdfMode}
                onRemove={onRemoveSkill}
              />
            </div>
          ))}
        {(!category.skillTags || category.skillTags.length === 0) && (
          <div className="text-gray-400 italic">No skills in this category</div>
        )}
      </div>
    </div>
  );
};

export const SkillSummary = ({
  skillCategoryBlocks,
  displayIcons = true,
  pdfMode,
  onRemoveSkill,
}: SkillsDisplayProps) => {
  // Helper function to split skills into rows of two
  const getSkillRows = () => {
    const rows = [];
    for (let i = 0; i < skillCategoryBlocks.length; i += 2) {
      rows.push(skillCategoryBlocks.slice(i, i + 2));
    }
    return rows;
  };

  return (
    <div className="flex flex-col gap-4">
      {getSkillRows().map((row) => (
        <div
          key={row.map((c) => c.title).join("|")}
          className="flex flex-col gap-4 mb-2 justify-between"
        >
          {row
            .filter((c) => c.skillTags && c.skillTags.length > 0)
            .map((category) => (
              <CategoryBlock
                key={category.title}
                category={category}
                displayIcons={displayIcons}
                pdfMode={pdfMode}
                onRemoveSkill={onRemoveSkill}
              />
            ))}
          {row.length === 1 && <div className="flex-1 min-w-0" />}
        </div>
      ))}
    </div>
  );
};
